import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  verticalCompactor,
  Responsive as ResponsiveGridLayout,
  useContainerWidth,
} from "react-grid-layout";
import type { ResponsiveLayouts } from "react-grid-layout";
import { getDashboardByCodeApi } from "src/api/dashboardApi";
import type { DashboardPageResponse } from "src/types/dashboard";
import {
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_CONTAINER_PADDING,
  GRID_MARGIN,
} from "src/constants/dashboard";
import WidgetRenderer from "src/components/dashboard/WidgetRenderer";
import DataContextBar from "src/components/dashboard/DataContextBar";
import { toLayoutItem } from "src/helpers/Dashboard";
import useDashboardStore from "src/store/dashboardStore";
import useFilterStore from "src/store/filterStore";
import FilterBar from "src/components/filters";
import { applyFilterDateOffset, getInitialValues } from "src/helpers/Filter";

export default function DashboardPage() {
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string | number>>({});
  const [filterDisplayValues, setFilterDisplayValues] = useState<Record<string, string | number>>({});

  const { dashboard_name } = useParams<{ dashboard_name: string }>();
  const { width, containerRef, mounted } = useContainerWidth({
    initialWidth: 900,
  });
  const { defaultDashboard } = useDashboardStore();
  const { savedFilters, updateSavedFilters, resetSavedFilters } = useFilterStore();
  const navigate = useNavigate();

  const {
    data: dashboardResponse,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = useQuery<DashboardPageResponse, Error>({
    queryKey: ["dashboard_name", dashboard_name],
    queryFn: async () => getDashboardByCodeApi(dashboard_name as string),
    enabled: !!dashboard_name,
  });

  // Filters chosen by the user persist across dashboards via the filter store.
  // Each dashboard only picks up the saved keys it declares itself, so stale
  // values from other dashboards never leak into its widget queries.
  const setInitialFilters = (useSaved = true) => {
    if (dashboardResponse?.filters) {
      const initialFilterValues = getInitialValues(dashboardResponse.filters);
      const carriedOver = useSaved
        ? Object.fromEntries(
            Object.entries(savedFilters).filter(
              ([key]) => key in initialFilterValues,
            ),
          )
        : {};
      const values = { ...initialFilterValues, ...carriedOver };
      setFilterDisplayValues(values);
      setAppliedFilters(applyFilterDateOffset(values));
    }
  }

  useEffect(() => {
    setInitialFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardResponse]);

  const visibleWidgets = useMemo(
    () =>
      (dashboardResponse?.widgets ?? [])
        .filter((widget) => widget.is_visible)
        .sort((a, b) => a.position - b.position),
    [dashboardResponse?.widgets],
  );

  const layouts = useMemo<ResponsiveLayouts>(() => {
    const baseLayout = visibleWidgets.map(toLayoutItem);
    const cloneLayout = () => baseLayout.map((item) => ({ ...item }));

    return {
      lg: cloneLayout(),
      md: cloneLayout(),
      sm: cloneLayout(),
      xs: cloneLayout(),
      xxs: cloneLayout(),
    };
  }, [visibleWidgets]);

  useEffect(() => {
    if (!dashboard_name) navigate(`/dashboards/${defaultDashboard}`);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard_name, defaultDashboard]);

  return (
      <div className="container-fluid px-5 py-3 overflow-y-auto overflow-x-hidden">
          <div className="d-flex align-items-center">
              <h1 className="auth-title me-2 mb-0">
                  {dashboardResponse?.dashboard?.name ?? "Dashboard"}
              </h1>
              </div>

          {!dashboard_name && (
              <p className="text-muted mb-0">Select a dashboard from the header.</p>
          )}

          {!!dashboard_name && isDashboardLoading && (
              <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
                  <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                  </div>
              </div>
          )}

          {!!dashboard_name && isDashboardError && (
              <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
                  <p className="fs-5 mb-0">Failed to load dashboard details.</p>
              </div>
          )}

          {!!dashboard_name &&
              !isDashboardLoading &&
              !isDashboardError &&
              visibleWidgets.length === 0 && (
                  <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
                      <p className="fs-5 mb-0">
                          No visible widgets configured for this dashboard.
                      </p>
                  </div>
              )
          }
          <div className="dashboard-stats row gap-1">
            {!!dashboard_name &&
                !isDashboardLoading &&
                !isDashboardError &&
                visibleWidgets.length > 0 &&(
                    <DataContextBar appliedFilters={appliedFilters} />
                )
            }

              {!!dashboard_name &&
              !isDashboardLoading &&
              !isDashboardError &&
              dashboardResponse?.filters && dashboardResponse?.filters.length > 0 &&
              visibleWidgets.length > 0 ? (
                  <div className="col">
                    <div className="card left-panel p-3 shadow">
                      <FilterBar
                          inputFields={dashboardResponse.filters}
                          defaultValues={filterDisplayValues}
                          isLoading={isDashboardLoading}
                          autoSubmitOnTabChange
                          onClear={() => {
                            resetSavedFilters();
                            setInitialFilters(false);
                          }}
                          onSubmit={({ fields }) => {
                            setFilterDisplayValues(prev => ({ ...prev, ...fields }));
                            setAppliedFilters(prev => ({ ...prev, ...applyFilterDateOffset(fields) }));
                            updateSavedFilters(fields);
                          }}
                      />
                    </div>
                  </div>
              ) : null
              }
            <div className="dashboard-content flex-fill col-9" ref={containerRef}>
              {/*{!!dashboard_name &&*/}
              {/*    !isDashboardLoading &&*/}
              {/*    !isDashboardError &&*/}
              {/*    visibleWidgets.length > 0 &&(*/}
              {/*    <DataContextBar appliedFilters={appliedFilters} />*/}
              {/*)}*/}

          {!!dashboard_name &&
              !isDashboardLoading &&
              !isDashboardError &&
              visibleWidgets.length > 0 &&
                  mounted && (
                      <ResponsiveGridLayout
                          width={width}
                          layouts={layouts}
                          breakpoints={GRID_BREAKPOINTS}
                          cols={GRID_COLS}
                          rowHeight={72}
                          margin={GRID_MARGIN}
                          containerPadding={GRID_CONTAINER_PADDING}
                          compactor={verticalCompactor}
                          dragConfig={{ enabled: false }}
                          resizeConfig={{ enabled: false }}
                      >
                        {visibleWidgets.map((widget) => {
                          return (
                              <WidgetRenderer
                                  key={String(widget.id)}
                                  className="flex-fill"
                                  widget={widget}
                                  dashboard_name={dashboard_name as string}
                                  appliedFilters={appliedFilters}

                              />
                          );
                        })}
                      </ResponsiveGridLayout>
                  )
              }
            </div>
          </div>
      </div>
  );
}
